import { useMemo, useState } from 'react';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { faClone, faPlus, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';

import Utils from '../../../../../../utils/immersive_utils';
import { useThemeMode } from '../../../../../../providers/theme-provider';
import {
  dilettaPanelSx,
  DILETTA_TITLE,
  DILETTA_DIVIDER,
} from '../../../../../../components/v2/widgets/exotics-shared';
import { IPortfolioPerformanceAccountReport } from '../../../../../../providers/portfolio-performance/portfolio-performance.types';

interface BacktestOverviewTableProps {
  reports: Record<string, IPortfolioPerformanceAccountReport | null>;
  strategyNames: string[];
  selectedStrategy: string;
  onSelectStrategy: (name: string) => void;
  // Backtests configured but not yet run — rendered with an explicit "Not run"
  // state across the metric columns rather than blank dashes (which read as a
  // data gap). Empty/omitted once every backtest in the study has results.
  notRunNames?: Set<string>;
  // Backtests mid per-variant Run — rendered with a "Running…" state across the
  // metric columns. Takes precedence over both metrics and "Not run".
  runningNames?: Set<string>;
  // Study-level set-creation, surfaced where the user sees the set. Per-row
  // duplicate makes a variant of that backtest; add creates a fresh one;
  // delete removes that backtest (hidden on the last row — a study keeps >= 1).
  onDuplicate?: (name: string) => void;
  onDelete?: (name: string) => void;
  onAddBacktest?: () => void;
  // Study-level backtest window (shared by every variant) — surfaced once as a
  // caption above the table, not repeated per row.
  backtestPeriod?: { start: Date | null; end: Date | null };
}

// Study-level backtest window, matching the top result header's format
// (backtest-result-header). UTC, date-only — the window is what every variant
// shares, so it reads as one caption above the table.
const formatBacktestPeriod = (start: Date | null, end: Date | null): string | null => {
  if (!start || !end) return null;
  return `${moment.utc(start).format('YYYY-MM-DD')} to ${moment.utc(end).format('YYYY-MM-DD')}`;
};

type SortKey = 'name' | 'pv' | 'daily' | 'mtd' | 'ytd' | 'maxDD' | 'sharpe' | 'sortino';

interface OverviewRow {
  name: string;
  denomination: string | null;
  pv: number | null;
  daily: number | null;
  mtd: number | null;
  ytd: number | null;
  maxDD: number | null;
  sharpe: number | null;
  sortino: number | null;
}

// Same 8-slot rhythm as the Portfolio Performance overview table
// (portfolio-performance-overview-table): 1 wide identity slot + 7 metric
// slots. A backtest stratlet reads with the exact same columns a live
// account gets.
const GRID_TEMPLATE = '2.2fr repeat(7, 1fr)';

const formatCurrency = (value: number | null, denomination: string | null): string => {
  if (value === null) return '-';
  return denomination === 'USD' || denomination === 'USDC'
    ? Utils.valueToCurrency(value, 2)
    : Utils.formatNumber(value, 4);
};

const formatPct = (value: number | null, decimals: number): string => {
  if (value === null) return '-';
  const pct = value * 100;
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(decimals)}%`;
};

const formatRatio = (value: number | null): string => (value === null ? '-' : value.toFixed(2));

const pctColour = (value: number | null, isDark: boolean): string | undefined => {
  if (value === null) return undefined;
  return value >= 0 ? (isDark ? '#4ade80' : '#166534') : isDark ? '#f87171' : '#991b1b';
};

const compareNullable = (a: number | null, b: number | null): number => {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
};

// Leaderboard of every stratlet in the run — one row each, sortable, click to
// drive the detail views below (equity / stats / performance). Mirrors the PP
// overview table's look and columns, but decoupled from the PP data layer:
// it can't reuse that component directly because it reads from
// PortfolioPerformanceContext and re-derives stats via buildStatsHistorical,
// which hard-slices to STATS_YEAR (2026) — a backtest window sits in the past,
// so that slice would zero out every stat. Here the metrics come straight off
// buildBacktestReport's own full-window figures instead.
export const BacktestOverviewTable = ({
  reports,
  strategyNames,
  selectedStrategy,
  onSelectStrategy,
  notRunNames,
  runningNames,
  onDuplicate,
  onDelete,
  onAddBacktest,
  backtestPeriod,
}: BacktestOverviewTableProps) => {
  const { resolvedMode } = useThemeMode();
  const isDark = resolvedMode === 'dark';
  const titleColour = isDark ? DILETTA_TITLE : '#092D3A';
  const labelColour = isDark ? 'rgba(194,229,255,0.5)' : 'rgba(9,45,58,0.5)';
  const subColour = isDark ? 'rgba(194, 229, 255, 0.55)' : 'rgba(9, 45, 58, 0.55)';
  const backtestPeriodLabel = backtestPeriod
    ? formatBacktestPeriod(backtestPeriod.start, backtestPeriod.end)
    : null;

  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(key === 'name' ? 'asc' : 'desc');
  };

  const rows = useMemo<OverviewRow[]>(
    () =>
      strategyNames.map((name) => {
        const report = reports[name];
        return {
          name,
          denomination: report?.denomination ?? null,
          pv: report?.pvRaw ?? null,
          daily: report?.dailyReturnRaw ?? null,
          mtd: report?.mtdReturnRaw ?? null,
          ytd: report?.ytdReturnRaw ?? null,
          maxDD: report?.maxDDRaw ?? null,
          sharpe: report?.sharpeRaw ?? null,
          sortino: report?.sortinoRaw ?? null,
        };
      }),
    [reports, strategyNames],
  );

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    return [...rows].sort((a, b) => {
      const cmp =
        sortKey === 'name' ? a.name.localeCompare(b.name) : compareNullable(a[sortKey], b[sortKey]);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [rows, sortKey, sortDir]);

  // A study must always keep at least one backtest, so delete is disabled when
  // this is the only row (also enforced in the provider).
  const canDeleteRow = sortedRows.length > 1;

  const HeaderCell = ({
    label,
    sortKeyFor,
    align = 'right',
  }: {
    label: string;
    sortKeyFor: SortKey;
    align?: 'left' | 'right';
  }) => (
    <Box
      onClick={() => toggleSort(sortKeyFor)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <Typography
        sx={{ fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: labelColour }}
      >
        {label}
      </Typography>
      {sortKey === sortKeyFor && (
        <Typography sx={{ fontSize: 8, color: labelColour }}>
          {sortDir === 'asc' ? '▲' : '▼'}
        </Typography>
      )}
    </Box>
  );

  const MetricCell = ({ value, color }: { value: string; color?: string }) => (
    <Typography
      sx={{
        fontSize: 13,
        fontWeight: 600,
        textAlign: 'right',
        color: color ?? titleColour,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {value}
    </Typography>
  );

  return (
    <Box sx={{ ...dilettaPanelSx, p: 2.5, mb: 2 }}>
      <Box
        sx={{
          mb: 1.5,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box>
          <Typography sx={{ color: titleColour, fontSize: 15, fontWeight: 700 }}>
            Overview
          </Typography>
          <Typography sx={{ color: subColour, fontSize: 12 }}>
            Click a column label to sort, click a row to load it below
          </Typography>
          {backtestPeriodLabel && (
            <Typography sx={{ color: subColour, fontSize: 12, mt: 0.25 }}>
              <Box component="span" sx={{ color: labelColour, letterSpacing: '0.04em' }}>
                Backtest period:{' '}
              </Box>
              {backtestPeriodLabel}
            </Typography>
          )}
        </Box>
        {onAddBacktest && (
          <Button
            onClick={onAddBacktest}
            startIcon={<FontAwesomeIcon icon={faPlus} style={{ fontSize: 11 }} />}
            sx={{
              textTransform: 'none',
              fontSize: 12,
              fontWeight: 600,
              color: titleColour,
              borderColor: DILETTA_DIVIDER,
              px: 1.25,
              py: 0.5,
              flexShrink: 0,
            }}
            variant="outlined"
            size="small"
          >
            Add variant
          </Button>
        )}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: GRID_TEMPLATE, gap: 1, px: 0.5, pb: 1 }}>
        <HeaderCell label="Backtest" sortKeyFor="name" align="left" />
        <HeaderCell label="PV" sortKeyFor="pv" />
        <HeaderCell label="Daily" sortKeyFor="daily" />
        <HeaderCell label="MTD" sortKeyFor="mtd" />
        <HeaderCell label="YTD" sortKeyFor="ytd" />
        <HeaderCell label="Max DD" sortKeyFor="maxDD" />
        <HeaderCell label="Sharpe" sortKeyFor="sharpe" />
        <HeaderCell label="Sortino" sortKeyFor="sortino" />
      </Box>

      <Box sx={{ borderTop: `1px solid ${DILETTA_DIVIDER}`, mb: 0.5 }} />

      {sortedRows.map((row) => (
        <Box
          key={row.name}
          onClick={() => onSelectStrategy(row.name)}
          sx={{
            display: 'grid',
            gridTemplateColumns: GRID_TEMPLATE,
            alignItems: 'center',
            gap: 1,
            px: 0.5,
            py: 1,
            cursor: 'pointer',
            borderRadius: '8px',
            backgroundColor:
              row.name === selectedStrategy ? 'rgba(2, 106, 162, 0.16)' : 'transparent',
            '&:hover': {
              backgroundColor:
                row.name === selectedStrategy
                  ? 'rgba(2, 106, 162, 0.20)'
                  : 'rgba(2, 106, 162, 0.08)',
            },
            '&:hover .overview-row-actions': { opacity: 1 },
            transition: 'background-color 120ms ease',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: titleColour,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {row.name}
            </Typography>
            {(onDuplicate || onDelete) && (
              <Box
                className="overview-row-actions"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                  opacity: 0,
                  transition: 'opacity 120ms ease',
                }}
              >
                {onDuplicate && (
                  <Tooltip title="Duplicate as a new variant">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDuplicate(row.name);
                      }}
                      sx={{ color: labelColour, '&:hover': { color: titleColour } }}
                    >
                      <FontAwesomeIcon icon={faClone} style={{ fontSize: 12 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {onDelete && (
                  <Tooltip
                    title={canDeleteRow ? 'Delete' : 'A study must keep at least one backtest'}
                  >
                    <span style={{ display: 'inline-flex' }}>
                      <IconButton
                        size="small"
                        disabled={!canDeleteRow}
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(row.name);
                        }}
                        sx={{ color: labelColour, '&:hover': { color: '#dc2626' } }}
                      >
                        <FontAwesomeIcon icon={faTrash} style={{ fontSize: 12 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
          {runningNames?.has(row.name) ? (
            <Typography
              sx={{
                gridColumn: '2 / -1',
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                textAlign: 'right',
                color: labelColour,
              }}
            >
              Running…
            </Typography>
          ) : notRunNames?.has(row.name) ? (
            <Typography
              sx={{
                gridColumn: '2 / -1',
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                textAlign: 'right',
                color: labelColour,
              }}
            >
              Not run
            </Typography>
          ) : (
            <>
              <MetricCell value={formatCurrency(row.pv, row.denomination)} />
              <MetricCell value={formatPct(row.daily, 2)} color={pctColour(row.daily, isDark)} />
              <MetricCell value={formatPct(row.mtd, 2)} color={pctColour(row.mtd, isDark)} />
              <MetricCell value={formatPct(row.ytd, 1)} color={pctColour(row.ytd, isDark)} />
              <MetricCell value={formatPct(row.maxDD, 1)} />
              <MetricCell value={formatRatio(row.sharpe)} />
              <MetricCell value={formatRatio(row.sortino)} />
            </>
          )}
        </Box>
      ))}
    </Box>
  );
};
